
import { Component, DestroyRef, inject, Inject, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { debounceTime } from 'rxjs/operators';
import { filter } from 'rxjs';
import { MakersiteService } from '@services/makersite.service';
import { ToastrService } from '@services/toaster.service';
import { GlobalDataService } from '@services/global-data.service';
import { DropdownMenuComponent, ObjectCardComponent, SearchBarComponent } from '@mds/ui';
import { CommonDialogWrapperComponent } from '../common-dialog-wrapper/common-dialog-wrapper.component';
import { PerfectScrollbarModule } from 'perfect-scrollbar-angular';
import { MatSelectModule } from '@angular/material/select';
import { CommonDialogButton } from '../common-dialog-wrapper/common-dialog-wrapper.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  InlineFormSchemaDialogComponent
} from '@dialogs/inline-form-schema-dialog/inline-form-schema-dialog.component';
import { RouterHelperService } from '@services/router-helper.service';

@Component({
  selector: 'app-pinset-search-dialog',
  standalone: true,
  imports: [
    CommonDialogWrapperComponent,
    PerfectScrollbarModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    ObjectCardComponent,
    SearchBarComponent,
    DropdownMenuComponent
  ],
  templateUrl: './pinset-search-dialog.component.html',
  styleUrls: ['./pinset-search-dialog.component.scss']
})
export class PinsetSearchDialogComponent implements OnInit {
  searchText = new UntypedFormControl();

  selectedItem = '';
  pinsetData: any;
  nodeDetails;
  filterOptions = [
    { text: 'All', value: 'all' },
    { text: 'Products', value: 'local-product' },
    { text: 'Production processes', value: 'comp-rules' },
    { text: 'Suppliers/Sites', value: 'local-supplier' },
    { text: 'Restricted Substance Lists', value: 'display-RSL' }
  ];
  cancelButton: CommonDialogButton = { text: 'Cancel', type: 'primary' };
  selectedType = new FormControl([this.filterOptions[0]]);
  selected: any;
  action: any;
  message: any;
  title = 'Select Substitute';
  private readonly destroyRef = inject(DestroyRef);
  private readonly routerHelperService = inject(RouterHelperService);
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private makersiteService: MakersiteService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private globalDataService: GlobalDataService,

    public dialogClose: MatDialogRef<PinsetSearchDialogComponent>
  ) {}

  ngOnInit(): void {
    this.action = this.data.action;
    this.nodeDetails = this.data.node;
    this.selectedType.valueChanges.subscribe({
      next: (v) => {
        if (this.searchText.value && this.searchText.value !== '') {
          this.pinsetSearch(this.searchText.value);
        }
      },
      error: (err) => console.log(err)
    });
    this.init(this.nodeDetails);
  }
  init(node) {
    const rid = node.rid.replace('#', '');
    const ridOriginal = node.rid_original?.replace('#', '') || '';
    if (ridOriginal !== rid && !this.selectedItem.includes(rid)) {
      this.selectedItem = `${this.selectedItem}&alt=${rid}&rid_original=${ridOriginal}`;
    }

    if (this.action === 'alternatives') {
      const { sourceLinks, targetLinks, ...payloadNode } = node;
      this.makersiteService.getSankeyAlternativeData(payloadNode).subscribe((res: any) => {
        if (res.lines) this.pinsetData = res.lines;
        else this.message = res.message;
      });
      this.title = 'View Alternatives';
    } else {
      this.makersiteService
        .getmodulePinData(
          rid,
          `${this.selectedType.value[0].value}${this.selectedItem}`,
          this.globalDataService.getPPMLayer().layer
        )
        .subscribe((res: any) => {
          this.pinsetData = res.radio;
          this.setPinsetCardType();
        });
    }
    this.searchText.valueChanges
      .pipe(
        filter((text: string) => text && text.length >= 1),
        debounceTime(500)
      )
      .subscribe({
        next: (v) => {
          this.pinsetSearch(v);
        },
        error: (err) => console.log(err)
      });

    // for dropdown
  }

  pinsetSearch(searchText) {
    searchText = searchText?.trim();
    const rid = this.nodeDetails.rid.replace('#', '');
    const ridOriginal = this.nodeDetails.rid_original?.replace('#', '') || '';
    if (searchText === '' || searchText === null) {
      this.init(this.nodeDetails);
    } else {
      if (ridOriginal !== rid && !this.selectedItem.includes(rid)) {
        this.selectedItem = `${this.selectedItem}&alt=${rid}&rid_original=${ridOriginal}`;
      }
      const layer = encodeURI(this.globalDataService.getPPMLayer().layer);
      this.selected = this.selectedType.value[0].text;
      this.makersiteService
        .pinsetSearchModal(searchText, rid, `${this.selectedType.value[0].value}${this.selectedItem}`, layer)
        .subscribe((res: any) => {
          this.pinsetData = res.radio;
          this.setPinsetCardType();
        });
    }
  }

  setPinsetCardType() {
    this.pinsetData.forEach((item) => {
      item.cardType = item.type;
    });
  }

  closeModal(data?: any) {
    this.dialogClose.close(data);
  }

  alternativeFormSchem(res) {
    const dialogRef = this.dialog.open(InlineFormSchemaDialogComponent, {
      panelClass: 'inline-schema',
      data: res
    });
    const instance = dialogRef.componentInstance;

    instance.submitDataEmitter.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      if (data && data.value.Inputs) {
        const payload = data.value.Inputs;
        this.makersiteService.submitEditedMetadata(this.nodeDetails.rid, payload).subscribe((res) => {
          this.closeModal();
        });
      }
    });
  }

  addFilter(data) {
    if (this.action === 'alternatives') {
      if (!data.link) return;
      this.makersiteService
        .linkApiCall(data.link.method, `/api/v1/${data.link.url}`, JSON.parse(data.link.payload))
        .subscribe((res: any) => {
          this.toastrService.showMessage(res.message);
        });
    } else {
      const obj = {
        data,
        pinGridSchema: this.data.gridSchema,
        nodeDetails: this.nodeDetails,
        action: this.action
      };

      if (this.data.isFrom === 'ngx') {
        this.closeModal(obj);
        return;
      }
    }
    this.closeModal();
  }

  openMenuItem(menuAction) {
    this.routerHelperService.openMenuItem(menuAction);
  }
}
